<template>
  <AuthLayout
    title="Forgot Your Password?"
    description="Enter the email linked to your account, and we'll send you a link to reset your password."
  >
    <div>
      <!-- Success Message -->
      <div v-if="success" class="p-4 mb-4 bg-green-50 border border-green-200 rounded-lg">
        <div class="flex">
          <div class="flex-shrink-0">
            <CheckIcon class="w-5 h-5 text-green-400" />
          </div>
          <div class="ml-3">
            <h3 class="text-sm font-medium text-green-800">Reset Link Sent!</h3>
            <div class="mt-2 text-sm text-green-700">
              <p>If an account exists with this email, we've sent a password reset link. Please check your email.</p>
            </div>
          </div>
        </div>
      </div>

      <!-- Error Message -->
      <div v-if="error" class="p-4 mb-4 bg-red-50 border border-red-200 rounded-lg">
        <div class="flex">
          <div class="flex-shrink-0">
            <OctagonExclamationIcon class="w-5 h-5 text-red-400" />
          </div>
          <div class="ml-3">
            <h3 class="text-sm font-medium text-red-800">Error Sending Reset Link</h3>
            <div class="mt-2 text-sm text-red-700">
              <p>{{ error }}</p>
            </div>
          </div>
        </div>
      </div>

      <!-- Form (only show if not success) -->
      <form v-if="!success" @submit.prevent="handleSubmit">
        <div class="space-y-5">
          <TextInputDefault
            labelText="Email"
            placeholder="Enter your Email"
            id="loginEmail"
            name="loginemail"
            type="email"
            v-model="form.email"
            :asterisk="true"
            :errorMessage="errors.email"
            @update:modelValue="clearError('email')"
          >
            <template #icon>
              <EnvelopeIcon />
            </template>
          </TextInputDefault>

          <BaseButton
            type="submit"
            :text="submitting ? 'Sending...' : 'Send Reset Link'"
            variant="primary"
            size="md"
            class="w-full"
            :disabled="submitting"
            :loading="submitting"
          />
        </div>
      </form>

      <!-- Success Actions -->
      <div v-if="success" class="mt-5">
        <div class="flex justify-center space-x-4">
          <BaseButton
            text="Go to Login"
            variant="secondary"
            size="sm"
            @click="$router.push('/')"
          />
          <BaseButton
            text="Resend Email"
            variant="primary"
            size="sm"
            @click="resendEmail"
            :loading="resending"
          />
        </div>
      </div>

      <!-- Login Link -->
      <div v-if="!success" class="mt-5">
        <p class="text-sm font-normal text-center text-gray-700">
          Wait, I remember my password...
          <router-link to="/" class="text-blue-600 hover:text-blue-500 font-medium">
            Click here
          </router-link>
        </p>
      </div>
    </div>
  </AuthLayout>
</template>

<script setup lang="ts">
import { ref, reactive } from 'vue';
import AuthLayout from '@/components/layout/AuthLayout.vue';
import TextInputDefault from '@/components/forms/TextInputDefault.vue';
import EnvelopeIcon from '@/icons/EnvelopeIcon.vue';
import BaseButton from '@/components/Buttons/BaseButton.vue';
import { UserApiService } from '@/services/userApiService';
import { CheckIcon, OctagonExclamationIcon } from '@/icons';

// Form state
const form = reactive({
  email: ''
});

const errors = reactive({
  email: ''
});

const submitting = ref(false);
const resending = ref(false);
const success = ref(false);
const error = ref('');

// Validate email
const validateEmail = (email: string): boolean => {
  const emailRegex = /^[^\s@]+@[^\s@]+\.[^\s@]+$/;
  return emailRegex.test(email);
};

// Validate form
const validateForm = (): boolean => {
  let isValid = true;
  
  // Clear previous errors
  errors.email = '';
  error.value = '';

  if (!form.email) {
    errors.email = 'Email is required';
    isValid = false;
  } else if (!validateEmail(form.email)) {
    errors.email = 'Please enter a valid email address';
    isValid = false;
  }

  return isValid;
};

// Clear specific error
const clearError = (field: keyof typeof errors) => {
  errors[field] = '';
  error.value = '';
};

// Define error types
interface ApiError {
  message: string;
  status?: number;
}

// Helper function to extract error message
const getErrorMessage = (err: unknown): string => {
  if (err instanceof Error) {
    return err.message;
  } else if (typeof err === 'object' && err !== null && 'message' in err) {
    const apiError = err as ApiError;
    return apiError.message || 'An unknown error occurred';
  } else if (typeof err === 'string') {
    return err;
  }
  return 'An unknown error occurred';
};

// Handle form submission
const handleSubmit = async () => {
  if (!validateForm()) {
    return;
  }

  submitting.value = true;
  error.value = '';

  try {
    // Use the requestPasswordReset method from UserApiService
    await UserApiService.requestPasswordReset(form.email);
    success.value = true;
    
  } catch (err: unknown) {
    const errorMessage = getErrorMessage(err);
    error.value = errorMessage || 'Failed to send reset link. Please try again.';
    console.error('Password reset request error:', err);
  } finally {
    submitting.value = false;
  }
};

// Resend email
const resendEmail = async () => {
  if (!form.email) {
    error.value = 'Email is required';
    return;
  }

  resending.value = true;
  error.value = '';

  try {
    await UserApiService.requestPasswordReset(form.email);
    
    // Show success message
    success.value = true;
    
  } catch (err: unknown) {
    const errorMessage = getErrorMessage(err);
    error.value = errorMessage || 'Failed to resend reset link.';
  } finally {
    resending.value = false;
  }
};
</script>